import * as React from 'react';
import * as Api from '@ViewModels';
import type { Edge, Node } from '@xyflow/react';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import type { BaseNodeData } from '../../../../web/containers/AutomationFlows/types';
import { convertTemplateToFlow } from '../../../../web/containers/AutomationFlows/utils/convertTemplateToFlow';

interface UseExpandedAutomationFlowParams {
	template: Api.IAutomationTemplateReference | null;
	useDraft: boolean;
	impersonationContext?: Api.IImpersonationContext;
}

interface CacheEntry {
	nodes: Node<BaseNodeData>[];
	edges: Edge[];
	templateUpdatedOn?: string;
}

export function useExpandedAutomationFlow({
	template,
	useDraft,
	impersonationContext,
}: UseExpandedAutomationFlowParams) {
	const userSession = useUserSession();
	const [nodes, setNodes] = React.useState<Node<BaseNodeData>[]>([]);
	const [edges, setEdges] = React.useState<Edge[]>([]);
	const [isExpanding, setIsExpanding] = React.useState(false);

	// Enhanced cache that tracks version timestamps
	const flowCacheRef = React.useRef<Map<string, CacheEntry>>(new Map());

	React.useEffect(() => {
		if (!template || !userSession) {
			setNodes([]);
			setEdges([]);
			setIsExpanding(false);
			return;
		}

		const templateId = template.id;
		// IAutomationTemplateReference can contain draft/published versions
		const templateWithVersions = template as unknown as Api.IAutomationTemplate;

		// Determine which version to use
		const version = useDraft ? templateWithVersions.draft : templateWithVersions.published;

		// If no version exists, clear state and return
		if (!version) {
			setNodes([]);
			setEdges([]);
			setIsExpanding(false);
			return;
		}

		// Generate cache key based on template ID, version, and layout direction
		// Note: IAutomationTemplateVersion doesn't have updatedOn, so we use template's updatedOn
		const templateUpdatedOn = (template as { updatedOn?: string }).updatedOn || '';
		const cacheKey = `${templateId}-${useDraft ? 'draft' : 'published'}-${templateUpdatedOn}`;

		// Check cache first
		const cachedEntry = flowCacheRef.current.get(cacheKey);
		if (cachedEntry) {
			setNodes(cachedEntry.nodes);
			setEdges(cachedEntry.edges);
			setIsExpanding(false);
			return;
		}

		// Expand flow using the shared converter
		const expandFlow = async () => {
			setIsExpanding(true);
			try {
				// Create a proper IAutomationTemplate structure for the converter
				const templateForConversion: Api.IAutomationTemplate = {
					id: template.id,
					name: template.name,
					draft: useDraft ? version : undefined,
					published: !useDraft ? version : undefined,
					status: template.status,
					templateType: Api.TemplateType.Automation,
				};

				// Use the shared converter with branch expansion
				const result = await convertTemplateToFlow(templateForConversion, {
					userSession,
					impersonationContext,
					expandBranches: true,
				});

				console.debug('[useExpandedAutomationFlow] Expanded template', {
					templateId,
					version: useDraft ? 'draft' : 'published',
					nodeCount: result.nodes.length,
					edgeCount: result.edges.length,
				});

				// Enrich all nodes with read-only flag for the testing canvas
				const enrichedNodes = result.nodes.map(node => ({
					...node,
					data: {
						...node.data,
						isReadOnly: true,
					},
				}));

				// Cache the result
				const cacheEntry: CacheEntry = {
					nodes: enrichedNodes,
					edges: result.edges,
					templateUpdatedOn,
				};
				flowCacheRef.current.set(cacheKey, cacheEntry);

				// Clear old cache entries to prevent memory leaks (keep last 10)
				if (flowCacheRef.current.size > 10) {
					const entries = Array.from(flowCacheRef.current.entries());
					const toDelete = entries.slice(0, entries.length - 10);
					toDelete.forEach(([key]) => flowCacheRef.current.delete(key));
				}

				setNodes(enrichedNodes);
				setEdges(result.edges);
			} catch (error) {
				console.error('[useExpandedAutomationFlow] Error expanding flow:', error);
				setNodes([]);
				setEdges([]);
			} finally {
				setIsExpanding(false);
			}
		};

		expandFlow();
	}, [template, useDraft, impersonationContext, userSession]);

	return { nodes, edges, isExpanding };
}
